<template>
    <div
        class="Infobox"
        :class="[{ 'is-active': isActive }, 'is-' + type, { 'is-inline': inline }, `size-${size}`]"
    >
        <div class="description-card">
            <i :class="iconClass"></i>
            <div class="description-content">
                <h4 v-if="title">{{ title }}</h4>
                <p v-if="message" :style="{ marginTop: title ? '8px' : '4px' }">{{ message }}</p>
                <slot></slot>
            </div>
        </div>
        <span v-if="hasCloseButton && !inline" class="Icon-close" @click="closeModal"></span>
    </div>
</template>
<script>
export default {
    props: {
        title: String,
        message: String,
        type: {
            type: String,
            default: 'warning',
        },
        hasCloseButton: {
            type: Boolean,
            default: true,
        },
        icon: {
            type: String,
            default: null,
        },
        inline: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'medium',
            validator: (value) => ['small', 'medium', 'large'].includes(value),
        },
    },
    data() {
        return {
            isActive: true,
        };
    },
    computed: {
        iconClass() {
            // If custom icon is provided, use it
            if (this.icon) {
                return this.icon;
            }

            // Otherwise use type-based icons
            const iconMap = {
                info: 'ri-information-line',
                warning: 'ri-error-warning-line',
                success: 'ri-check-line',
                error: 'ri-close-line',
            };
            return iconMap[this.type] || iconMap.warning;
        },
    },
    methods: {
        closeModal: function () {
            this.isActive = false;
            this.$emit('onClose');
        },
    },
};
</script>
<style lang="scss" scoped>
.Infobox {
    position: relative;
    transform: scale(0.85);
    opacity: 0;
    transition:
        transform 0.095s,
        opacity 0.15s 0.3s,
        max-height 0.2s 0.4s,
        padding 0.2s 0.4s,
        margin 0.2s 0.4s;
    max-height: 0;
    will-change: transform;
    transform-origin: center;
    margin-top: 20px;
    margin-bottom: 20px;

    @media (max-width: $breakpoint-tablet-portrait) {
        margin: 10px;
    }

    &.is-active {
        transform: scale(1);
        opacity: 1;
        max-height: 300px;

        @media (max-width: $breakpoint-tablet-portrait) {
            max-height: none;
        }
    }

    &.is-inline {
        transform: none;
        opacity: 1;
        max-height: none;
        margin: 8px 0;
        transition: none;

        .description-card {
            padding: 12px 16px;
        }
    }

    .description-card {
        display: flex;
        align-items: flex-start;
        padding: 20px;
        border-radius: 8px;
        position: relative;

        i {
            font-size: 2rem;
            margin-right: 15px;
            margin-top: 2px;
            flex-shrink: 0;
        }

        .description-content {
            flex: 1;

            h4 {
                margin: 0 0 8px 0;
                font-size: 1.1rem;
                font-weight: 600;
            }

            p {
                margin: 0 0 12px 0;
                font-size: 0.9rem;
                line-height: 1.5;
            }

            > *:last-child {
                margin-bottom: 0;
            }
        }
    }

    // Type-specific styling
    &.is-info .description-card {
        background-color: #e3f2fd;
        border: 0.5px solid #bbdefb;

        i {
            color: #1976d2;
        }

        h4 {
            color: #1565c0;
        }

        p {
            color: #424242;
        }
    }

    &.is-warning .description-card {
        background-color: #fff3e0;
        border: 0.5px solid #ffcc02;

        i {
            color: #f57c00;
        }

        h4 {
            color: #e65100;
        }

        p {
            color: #424242;
        }
    }

    &.is-success .description-card {
        background-color: #e8f5e8;
        border: 0.5px solid #c8e6c9;

        i {
            color: #388e3c;
        }

        h4 {
            color: #2e7d32;
        }

        p {
            color: #424242;
        }
    }

    &.is-error .description-card {
        background-color: #ffebee;
        border: 0.5px solid #ffcdd2;

        i {
            color: #d32f2f;
        }

        h4 {
            color: #c62828;
        }

        p {
            color: #424242;
        }
    }

    &.size-small {
        .description-card {
            padding: 8px 12px;

            i {
                font-size: 1.25rem;
                margin-right: 8px;
                margin-top: 1px;
            }

            .description-content {
                h4 {
                    font-size: 0.875rem;
                    margin: 0 0 4px 0;
                }

                p {
                    font-size: 0.75rem;
                    margin: 0 0 8px 0;
                }
            }
        }

        &.is-inline .description-card {
            padding: 6px 10px;
        }
    }

    &.size-large {
        .description-card {
            padding: 24px 28px;

            i {
                font-size: 2.5rem;
                margin-right: 20px;
                margin-top: 4px;
            }

            .description-content {
                h4 {
                    font-size: 1.25rem;
                    margin: 0 0 12px 0;
                }

                p {
                    font-size: 1rem;
                    margin: 0 0 16px 0;
                }
            }
        }

        &.is-inline .description-card {
            padding: 16px 20px;
        }
    }

    .Icon-close {
        background-color: rgba(0, 0, 0, 0.1);
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        right: 15px;
        top: 15px;
        opacity: 1;
        transform: scale(1);
        will-change: transform;
        cursor: pointer;
        border-radius: 50%;
        transition: background-color 0.2s ease;

        &:hover {
            background-color: rgba(0, 0, 0, 0.2);
        }

        &::before {
            position: absolute;
            width: 2px;
            height: 60%;
            content: '';
            transform: translate(-50%, -50%) rotateZ(45deg);
            left: 50%;
            top: 50%;
            background-color: #666;
        }

        &::after {
            position: absolute;
            width: 2px;
            height: 60%;
            content: '';
            left: 50%;
            transform: rotateZ(-45deg) translate(-120%, 10%);
            background-color: #666;
        }
    }
}
</style>
